/**
 * 公車路線模糊搜尋（與 backend/utils/bus-route-search.js 保持同步）
 */

import {
    TDX_CITY_ZH,
    normalizeBusCityCode,
    cityLabel,
    cleanBusRouteRef
} from './bus-route-display.js';

export { TDX_CITY_ZH, cityLabel };

function foldToken(value) {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

export function cityCodeFromRoute(row = {}) {
    const city = row.h_city_code || row.city_code;
    if (city && /^[A-Za-z]{3}$/.test(String(city))) return normalizeBusCityCode(city);
    const rid = String(row.route_id || row.tdx_route_uid || row.h_route_family_id || '');
    const m = rid.match(/^([A-Za-z]{3})/);
    return m ? normalizeBusCityCode(m[1]) : '';
}

function refKey(ref) {
    return foldToken(ref).replace(/路$/, '').replace(/區$/, '');
}

function charsInOrder(query, hay) {
    if (!query || !hay) return false;
    let i = 0;
    for (const ch of hay) {
        if (ch === query[i]) i += 1;
        if (i >= query.length) return true;
    }
    return false;
}

function tokenScore(token, row) {
    const t = foldToken(token);
    if (!t) return 0;
    const ref = foldToken(row.ref);
    const refBare = refKey(row.ref);
    const name = foldToken(row.name);
    const cityCode = foldToken(row.h_city_code || cityCodeFromRoute(row));
    const cityZh = foldToken(row.city_zh || cityLabel(cityCode));
    const dep = foldToken(row.h_departure);
    const dest = foldToken(row.h_destination);

    if (ref === t || refBare === t) return 100;
    if (/^\d+$/.test(t)) {
        const rest = ref.startsWith(t) ? ref.slice(t.length) : (refBare.startsWith(t) ? refBare.slice(t.length) : null);
        if (rest !== null) {
            if (rest === '') return 100;
            if (!/^\d/.test(rest)) return 86;
            if (t.length < 3) return 86;
        }
        if (cityZh === t || cityZh.includes(t) || cityCode === t) return 58;
        if (dep.includes(t) || dest.includes(t)) return 46;
        return 0;
    }
    if (ref.startsWith(t) || refBare.startsWith(t)) return 86;
    if (ref.includes(t) || refBare.includes(t)) return 72;
    if (cityZh === t || cityZh.includes(t) || cityCode === t) return 58;
    if (dep.includes(t) || dest.includes(t)) return 46;
    if (name.includes(t)) return 34;
    if (/^\d+$/.test(t)) return 0;
    if (charsInOrder(t, refBare) || charsInOrder(t, name) || charsInOrder(t, dep) || charsInOrder(t, dest)) {
        return 16;
    }
    return 0;
}

export function scoreBusRoute(row, query) {
    const tokens = String(query || '')
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    if (!tokens.length) return 0;
    let total = 0;
    for (const token of tokens) {
        const s = tokenScore(token, row);
        if (s <= 0) return 0;
        total += s;
    }
    return total;
}

function groupKey(row) {
    if (row.h_route_family_id) return String(row.h_route_family_id);
    const city = cityCodeFromRoute(row);
    return `${city}|${refKey(row.ref) || row.route_id}`;
}

export function enrichCatalogRow(row = {}) {
    const h_city_code = cityCodeFromRoute(row);
    const ref = cleanBusRouteRef(row.ref || row.name || '');
    const name = cleanBusRouteRef(row.name || row.ref || '') || ref;
    return {
        route_id: row.route_id,
        ref,
        name,
        h_city_code,
        city_zh: cityLabel(h_city_code),
        h_route_family_id: row.h_route_family_id || null,
        tdx_route_uid: row.tdx_route_uid || null,
        h_departure: row.h_departure || null,
        h_destination: row.h_destination || null,
        color: row.color || null
    };
}

export function buildBusRouteCatalog(routeToStops = {}) {
    return Object.values(routeToStops || {}).map((row) => enrichCatalogRow(row));
}

export function searchBusRoutes(catalog, query, opts = {}) {
    const limit = opts.limit ?? 24;
    const q = String(query || '').trim();
    if (!q) return [];
    const best = new Map();
    for (const raw of catalog || []) {
        const row = enrichCatalogRow(raw);
        const score = scoreBusRoute(row, q);
        if (score <= 0) continue;
        const key = groupKey(row);
        const prev = best.get(key);
        if (!prev || score > prev.score) best.set(key, { ...row, score });
    }
    return [...best.values()]
        .sort((a, b) => b.score - a.score || String(a.ref).localeCompare(String(b.ref), 'zh-Hant'))
        .slice(0, limit);
}
